/**
 * GIF frames for texture lines (W2-01a preview-gl; rendering.md R§3.5, ARCHITECTURE.md §7.3).
 *
 * Phira picks the frame of a GIF line from its own timing: the frame delays exactly as the decoder reads them
 * (Graphics Control Extension delay x 10 ms, NO clamping - browsers turn delays <= 10 ms into 100 ms, Phira does
 * not) and the position `gifMs` the engine computes from the GIF progress. The delays come from
 * `AssetMeta.gifDelaysMs` (parsed in the io worker); when the metadata is missing they are read from the GCE blocks
 * here, again unclamped. Browser animation timing is never used.
 *
 * Frame selection (`frameIndexAt`): with cumulative delays D_i = d_0 + ... + d_i, the frame at position `ms`
 * (taken modulo the total, negative -> 0) is the first i with ms < D_i; zero-delay frames are therefore never shown,
 * and a GIF whose delays sum to 0 shows its first frame (Phira hangs on it: the checker reports it, PH-04).
 *
 * Decoding (lazy, once per asset): `ImageDecoder` (WebCodecs) where available - it composites frames itself -,
 * else gifuct-js with our own compositing (disposal 0/1 keep, 2 clear the frame rectangle to transparent, 3 restore
 * the previous canvas; transparent pixels leave what is below). Frames become straight-alpha ImageBitmaps.
 */

import { decompressFrame, parseGIF, type ParsedGif } from 'gifuct-js';
import type { DecodedGif } from '../types';

/** Total of a delay list (ms). */
export function totalDelay(delaysMs: readonly number[]): number {
  let t = 0;
  for (const d of delaysMs) t += d > 0 ? d : 0;
  return t;
}

/** Frame index at `ms` within the loop (Phira's frame selection from raw delays). */
export function frameIndexAt(delaysMs: readonly number[], ms: number): number {
  const n = delaysMs.length;
  if (n <= 1) return 0;
  const total = totalDelay(delaysMs);
  if (!(total > 0)) return 0;
  let pos = Number.isFinite(ms) ? ms % total : 0;
  if (pos < 0) pos += total;
  let acc = 0;
  for (let i = 0; i < n; i++) {
    const d = delaysMs[i] ?? 0;
    acc += d > 0 ? d : 0;
    if (pos < acc) return i;
  }
  return n - 1;
}

/** A DecodedGif over decoded frames and Phira's delays (frame count = the shorter of both lists). */
export function createDecodedGif(frames: readonly ImageBitmap[], delaysMs: readonly number[]): DecodedGif {
  const n = Math.min(frames.length, delaysMs.length);
  const fs = frames.slice(0, Math.max(1, n));
  const ds = delaysMs.slice(0, n);
  const total = totalDelay(ds);
  return {
    frames: fs,
    delaysMs: ds,
    totalMs: total,
    frameAt: (ms: number) => Math.min(fs.length - 1, frameIndexAt(ds, ms)),
  };
}

function isGifFrame(f: ParsedGif['frames'][number]): f is Extract<ParsedGif['frames'][number], { image: unknown }> {
  return typeof f === 'object' && 'image' in f;
}

/**
 * Raw GCE delays of a GIF in ms (delay x 10, unclamped; 0 for a frame without a GCE), or null when the bytes are
 * not a readable GIF.
 */
export function parseGifDelays(bytes: Uint8Array): number[] | null {
  if (!isGifBytes(bytes)) return null;
  try {
    const gif = parseGIF(bytes.slice().buffer);
    const out: number[] = [];
    for (const f of gif.frames) {
      if (!isGifFrame(f)) continue;
      const gce = (f as { gce?: { delay?: unknown } }).gce;
      const d = typeof gce?.delay === 'number' ? gce.delay : 0;
      out.push(d * 10);
    }
    return out;
  } catch {
    return null;
  }
}

/** Whether the bytes start with a GIF signature ("GIF87a" / "GIF89a"). */
export function isGifBytes(bytes: Uint8Array): boolean {
  return bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46;
}

/** Composites gifuct-js frames into full RGBA frames (straight alpha). */
export function compositeGifFrames(bytes: Uint8Array): {
  width: number;
  height: number;
  frames: Uint8ClampedArray<ArrayBuffer>[];
} {
  if (!isGifBytes(bytes)) throw new Error('not a GIF file');
  const gif = parseGIF(bytes.slice().buffer);
  const width = Math.max(1, gif.lsd.width);
  const height = Math.max(1, gif.lsd.height);
  const canvas = new Uint8ClampedArray(width * height * 4);
  const frames: Uint8ClampedArray<ArrayBuffer>[] = [];
  let prevDisposal = 0;
  let prevRect: readonly [number, number, number, number] | null = null;
  let saved: Uint8ClampedArray<ArrayBuffer> | null = null;
  for (const raw of gif.frames) {
    if (!isGifFrame(raw)) continue;
    // Dispose of the previous frame before drawing this one.
    if (prevDisposal === 2 && prevRect !== null) {
      const [x0, y0, w, h] = prevRect;
      for (let y = Math.max(0, y0); y < Math.min(height, y0 + h); y++) {
        canvas.fill(0, (y * width + Math.max(0, x0)) * 4, (y * width + Math.min(width, x0 + w)) * 4);
      }
    } else if (prevDisposal === 3 && saved !== null) {
      canvas.set(saved);
    }
    const f = decompressFrame(raw, gif.gct, true);
    const disposal = (f as { disposalType?: number }).disposalType ?? 0;
    saved = disposal === 3 ? canvas.slice() : null;
    const { left, top, width: fw, height: fh } = f.dims;
    const patch = f.patch;
    for (let y = 0; y < fh; y++) {
      const cy = top + y;
      if (cy < 0 || cy >= height) continue;
      for (let x = 0; x < fw; x++) {
        const cx = left + x;
        if (cx < 0 || cx >= width) continue;
        const s = (y * fw + x) * 4;
        if ((patch[s + 3] ?? 0) === 0) continue;
        const d = (cy * width + cx) * 4;
        canvas[d] = patch[s] ?? 0;
        canvas[d + 1] = patch[s + 1] ?? 0;
        canvas[d + 2] = patch[s + 2] ?? 0;
        canvas[d + 3] = 255;
      }
    }
    frames.push(canvas.slice());
    prevDisposal = disposal;
    prevRect = [left, top, fw, fh];
  }
  return { width, height, frames };
}

const BITMAP_OPTIONS: ImageBitmapOptions = { premultiplyAlpha: 'none', colorSpaceConversion: 'none' };

async function decodeWithImageDecoder(bytes: Uint8Array): Promise<ImageBitmap[] | null> {
  if (typeof ImageDecoder !== 'function') return null;
  try {
    if (!(await ImageDecoder.isTypeSupported('image/gif'))) return null;
    const decoder = new ImageDecoder({ data: bytes.slice(), type: 'image/gif', colorSpaceConversion: 'none' });
    try {
      await decoder.tracks.ready;
      const track = decoder.tracks.selectedTrack;
      const count = track?.frameCount ?? 0;
      const out: ImageBitmap[] = [];
      for (let i = 0; i < count; i++) {
        const { image } = await decoder.decode({ frameIndex: i, completeFramesOnly: true });
        try {
          out.push(await createImageBitmap(image, BITMAP_OPTIONS));
        } finally {
          image.close();
        }
      }
      return out.length > 0 ? out : null;
    } finally {
      decoder.close();
    }
  } catch {
    return null;
  }
}

async function decodeWithGifuct(bytes: Uint8Array): Promise<ImageBitmap[]> {
  const { width, height, frames } = compositeGifFrames(bytes);
  const out: ImageBitmap[] = [];
  for (const px of frames) out.push(await createImageBitmap(new ImageData(px, width, height), BITMAP_OPTIONS));
  return out;
}

/**
 * Decodes a GIF. `delaysMs` are Phira's delays (AssetMeta.gifDelaysMs); null reads them from the bytes. Rejects when
 * the file cannot be decoded.
 */
export async function decodeGif(bytes: Uint8Array, delaysMs: readonly number[] | null): Promise<DecodedGif> {
  const frames = (await decodeWithImageDecoder(bytes)) ?? (await decodeWithGifuct(bytes));
  if (frames.length === 0) throw new Error('GIF without frames');
  return createDecodedGif(frames, delaysMs ?? parseGifDelays(bytes) ?? []);
}

/** Closes the frame bitmaps of a decoded GIF. */
export function closeGif(gif: DecodedGif): void {
  for (const f of gif.frames) f.close();
}
